import { selectItemById } from '@data/selectors/itemSelectors';

import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { formatRegistrationPlate, getVehicleFullName } from '@utils/vehicle';
import { Flex, Message } from '@kvdbil/components';
import ItemCard from '../components/ItemCard';
import { vehicleTypeChipSwitch } from '../components/VehicleTypeChipSwitch';
import DefaultSpinner from '@components/ui/DefaultSpinner';
import { GenerationStatus } from './types.ts';
import { UploadCard } from '../components/UploadCard.tsx';
import { deliveryReceiptSelector } from '@data/selectors/deliveryReceiptSelectors.ts';
import { useEffect } from 'react';

const Container = styled.div`
  overflow: initial;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

type UploadViewProps = {
  onGeneratingReceipt: (file: File | null) => void;
  generatingState: GenerationStatus;
};

const DeliveryReceiptView = ({
  onGeneratingReceipt,
  generatingState,
}: UploadViewProps) => {
  const { itemId } = useParams<{
    itemId: string;
  }>();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const item = useSelector(selectItemById(itemId));
  const deliveryReceipt = useSelector(deliveryReceiptSelector());
  useEffect(() => {
    if (
      deliveryReceipt.receipt &&
      itemId == deliveryReceipt.receipt.processObjectId
    ) {
      navigate(`/delivery-receipt/${itemId}/generated`);
    }
  }, [deliveryReceipt.receipt, itemId, navigate]);

  const formattedRegNr = formatRegistrationPlate(
    item?.properties?.registrationPlate ?? ''
  );

  const subTitle = item ? getVehicleFullName(item) : '-----';
  const title = `${item?.properties?.registrationPlate ? formattedRegNr + ' | ' : ''}${item?.id}`;
  const chipsData = item ? [vehicleTypeChipSwitch(t, item.vehicleType)] : [];

  if (!item) {
    return (
      <Flex grow={1}>
        <DefaultSpinner />
      </Flex>
    );
  }

  if (!item.isValidForDeliveryReceipt) {
    return (
      <Flex grow={1}>
        <Message fullWidth={true} type={'error'} withIcon={true} textCenter>
          {t('Generating delivery receipt is not possible for closed objects.')}
        </Message>
      </Flex>
    );
  }

  return (
    <Container>
      <Container>
        <ItemCard
          itemId={item.id}
          title={title}
          subTitle={subTitle}
          chips={chipsData}
        />

        {generatingState == GenerationStatus.IN_PROGRESS && (
          <Flex grow={1} direction={'column'}>
            <DefaultSpinner text={t('Creates delivery receipt')} />
          </Flex>
        )}

        {generatingState == GenerationStatus.ERROR && (
          <Message fullWidth={true} type={'error'} withIcon={true} textCenter>
            {t(
              "We couldn't generate delivery receipt. Please try again or contact support."
            )}
          </Message>
        )}

        {[GenerationStatus.NOT_STARTED, GenerationStatus.ERROR].includes(
          generatingState
        ) && (
          <UploadCard
            handleUploadButtonClick={file => onGeneratingReceipt(file)}
          />
        )}
      </Container>
    </Container>
  );
};

export default DeliveryReceiptView;
